import { defineStore } from 'pinia'
import * as Api from './api'
import {
  callWithMeta,
  createMeta,
  getMetaInitial,
  waitFor,
  type Meta,
} from '@/utils/store'

type State = {
  finishedTutorials: Record<number, Api.Tutorial>
  meta: Meta<{
    finishedTutorials: undefined
    loadTutorials: undefined
    finishTutorial: undefined
    deleteTutorials: undefined
  }>
}

const getDefaultState = (): State => ({
  finishedTutorials: {},
  meta: createMeta([
    'finishedTutorials',
    'loadTutorials',
    'finishTutorial',
    'deleteTutorials',
  ]),
})

export const useTutorialsStore = defineStore('tutorials', {
  state: getDefaultState,

  actions: {
    RESET() {
      this.finishedTutorials = {}
      this.meta.finishedTutorials = getMetaInitial()
    },

    async loadTutorials({ useCache = true } = {}) {
      const hasCached = ['pending', 'success'].includes(
        this.meta.finishedTutorials.status
      )

      if (useCache && hasCached) {
        await waitFor(() => this.meta.finishedTutorials.status !== 'pending')

        return
      }

      const { result } = await callWithMeta({
        store: this,
        metaFields: { finishedTutorials: undefined, loadTutorials: undefined },
        apiCall: async () => await Api.loadTutorials(),
        onSuccess: ({ result, isLatest }) => {
          if (isLatest('finishedTutorials')) {
            this.finishedTutorials = result.payload
          }
        },
      })

      if (result.status === 'error') {
        throw result.payload
      }
    },

    async finishTutorial(tutorialName: Api.Tutorial['name']) {
      if (
        Object.values(this.finishedTutorials)?.filter(
          (element) => element.name === tutorialName
        ).length
      ) {
        return
      }

      const randomId = (Math.random() * Number.MAX_SAFE_INTEGER) / 1

      const tutorial: Api.Tutorial = {
        name: tutorialName,
        state: 'finished',
        step: 1,
      }

      this.finishedTutorials[randomId] = tutorial

      const { result } = await callWithMeta({
        store: this,
        metaFields: { finishedTutorials: undefined, finishTutorial: undefined },
        apiCall: async () => await Api.createTutorial(tutorialName),
        onSuccess: ({ result, isLatest }) => {
          if (isLatest('finishedTutorials')) {
            delete this.finishedTutorials[randomId]

            this.finishedTutorials[result.payload.id] = tutorial
          }

          if (isLatest('finishTutorial')) {
            this.loadTutorials({ useCache: true })
          }
        },
        onError: ({ isLatest }) => {
          if (isLatest('finishTutorial')) {
            this.loadTutorials({ useCache: true })
          }
        },
      })

      if (result.status === 'error') {
        throw result.payload
      }
    },

    async deleteTutorials(tutorialNames: Api.Tutorial['name'][]) {
      const tutorialIds = Object.keys(this.finishedTutorials)
        .map((key) => Number(key))
        .filter((id) => tutorialNames.includes(this.finishedTutorials[id].name))

      tutorialIds.forEach((tutorialId) => {
        delete this.finishedTutorials[tutorialId]
      })

      await callWithMeta({
        store: this,
        metaFields: {
          finishedTutorials: undefined,
          deleteTutorials: undefined,
        },
        apiCall: async () => {
          const results = await Promise.all(
            tutorialIds.map(async (tutorialId) => {
              const result = await Api.deleteTutorial(tutorialId)

              if (result.status === 'error') {
                throw result.payload
              }
            })
          )

          return {
            status: 'success',
            payload: results,
          }
        },
        onSuccess: ({ isLatest }) => {
          if (isLatest('deleteTutorials')) {
            this.loadTutorials({ useCache: true })
          }
        },
        onError: ({ isLatest }) => {
          if (isLatest('deleteTutorials')) {
            this.loadTutorials({ useCache: true })
          }
        },
      })
    },
  },
})
